/**
 * How a co-traveller reads, separated from how it draws.
 *
 * The panel's whole job is ordering: in city traffic the alert list is either
 * empty or twenty rows long, and the operator's real question is which of the
 * things moving with them has been doing it long enough to matter. That
 * judgement is arithmetic on evidence the server already computed, so it lives
 * here where it can be tested without a map.
 */
import { CompanionTier, MissionCotraveler } from '../../services/missionApi';

/** Bright red is spent only on the top rung, so it keeps meaning something. */
export const TIER_COLOR: Record<CompanionTier, string> = {
  entrenched: '#ff3b30',
  sustained: '#ff6b35',
  persistent: '#f5b942',
  brief: '#8a8f98',
};

export const STATE_COLOR: Record<string, string> = {
  sustained: '#ff3b30',
  confirmed: '#ff3b30',
  emitted: '#ff3b30',
  suspected: '#f5b942',
  none: '#8a8f98',
  stale: '#5a6068',
};

export const STATE_LABEL: Record<string, string> = {
  sustained: 'Following',
  confirmed: 'Confirmed',
  emitted: 'Watchlisted',
  suspected: 'Suspected',
  none: 'Watching',
  stale: 'Lost',
};

/**
 * What to call the emitter.
 *
 * A network name is what an operator can act on — they can look out of the
 * window for a van with that hotspot. A MAC is a last resort, and shortening it
 * to the last three octets keeps the row readable without pretending the rest
 * does not exist.
 */
export function cotravelerName(c: MissionCotraveler): string {
  if (c.watchlist_label) return c.watchlist_label;
  if (c.name) return c.name;
  const id = c.identifier || c.threat_key.replace(/^cotraveler:/, '');
  const octets = id.split(':');
  return octets.length === 6 ? `…${octets.slice(3).join(':')}` : id;
}

/**
 * The one-line reason this row is where it is in the list.
 *
 * Deliberately the evidence rather than the score. "0.72" tells an operator
 * nothing they can weigh; "12 mi over 40m" is the thing they can sanity-check
 * against what they remember of the drive.
 */
export function cotravelerEvidence(
  c: MissionCotraveler,
  formatMeters: (m?: number | null) => string,
  formatSpan: (s?: number | null) => string,
): string {
  const parts: string[] = [];
  if (c.shared_distance_m) parts.push(`${formatMeters(c.shared_distance_m)} together`);
  if (c.shared_seconds) parts.push(`over ${formatSpan(c.shared_seconds)}`);
  if (c.distinct_segments) parts.push(`${c.distinct_segments} segments`);
  return parts.join(' · ');
}

/**
 * Short notes that change how the row should be read, in the order they matter.
 *
 * `survived_reroute` leads because it is the strongest inferential evidence
 * available: something still with us after the route deliberately changed is
 * following us and not the road. The corridor note is the opposite and has to
 * be visible for the same reason — a damped regular looks identical to a weak
 * candidate unless the panel says why it is being held back.
 */
export function cotravelerNotes(c: MissionCotraveler): string[] {
  const notes: string[] = [];
  if (c.survived_reroute) notes.push('Survived reroute');
  if (c.gap_fill) {
    notes.push(c.continued_from_label
      ? `Gap fill · was ${c.continued_from_label}`
      : 'Gap fill');
  }
  if (c.watchlist_id) notes.push('Watchlisted');
  if (c.corridor_regular_runs) {
    notes.push(`Corridor regular · ${c.corridor_regular_runs} prior ${
      c.corridor_regular_runs === 1 ? 'run' : 'runs'}`);
  }
  if (c.track === 'identity+inference') notes.push('Identity + inference');
  return notes;
}

/**
 * Whether this row deserves the operator's attention right now.
 *
 * Used for the tab's badge, so it counts what an alert would count rather than
 * everything the panel lists — a badge reading 23 in traffic is a badge nobody
 * looks at twice.
 */
export function isPressing(c: MissionCotraveler): boolean {
  if (c.state === 'stale') return false;
  if (c.watchlist_id) return true;
  return c.state === 'confirmed' || c.state === 'sustained';
}

/** Spoken line for the vehicle. Lost is still a report, not a lockout. */
export function followerAlertLine(
  c: MissionCotraveler,
  formatMeters: (m?: number | null) => string,
): string {
  const name = cotravelerName(c);
  const state = STATE_LABEL[c.state] || c.state;
  const together = c.shared_distance_m
    ? `${formatMeters(c.shared_distance_m)} together`
    : null;
  return [state, name, together].filter(Boolean).join('. ') + '.';
}

export function pressingCount(list: MissionCotraveler[]): number {
  return list.filter(isPressing).length;
}
